import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersService, Customer } from '../services/customersService';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Typography,
  Spin,
  Alert,
  Space,
  Row,
  Col,
  Tag,
  Avatar,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  CompassOutlined,
  SaveOutlined,
  CheckCircleFilled,
  CopyOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { snackActions } from '../utils/snackbarUtils';

const { Title, Text } = Typography;
const { Option } = Select;

const getStatusTag = (status?: string) => {
  switch (status) {
    case 'active':
      return <Tag color="success" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>✓ Actif</Tag>;
    case 'inactive':
      return <Tag color="default" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Inactif</Tag>;
    case 'pending':
      return <Tag color="warning" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>⏳ En attente</Tag>;
    case 'suspended':
      return <Tag color="error" style={{ padding: '2px 10px', borderRadius: 4, fontWeight: 500 }}>Suspendu</Tag>;
    default:
      return null;
  }
};

const CustomerForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(!!id);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [createdModalVisible, setCreatedModalVisible] = useState<boolean>(false);
  const [createdCustomer, setCreatedCustomer] = useState<Customer | null>(null);

  const selectedCity = Form.useWatch('city', form);
  const isAbidjan = selectedCity === 'Abidjan';

  useEffect(() => {
    if (id) {
      setLoading(true);
      customersService.getCustomerById(Number(id))
        .then((data) => {
          setCustomer(data);
          // Mapper company_type et gender pour correspondre aux options du Select
          const mappedCompanyType = data.company_type === 'household' ? 'particulier'
            : data.company_type === 'healthcare' ? 'sante'
            : data.company_type === 'enterprise' ? 'entreprise'
            : data.company_type;

          const mappedGender = data.gender === 'male' ? 'homme'
            : data.gender === 'female' ? 'femme'
            : data.gender === 'other' ? 'autre'
            : data.gender;

          form.setFieldsValue({
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email || data.user?.email || undefined,
            phone: data.phone || data.user?.phone,
            status: data.user?.status || data.status,
            company_name: data.company_name,
            company_type: mappedCompanyType,
            gender: mappedGender,
            country: data.country || "Côte d'Ivoire",
            city: data.city,
            commune: data.commune,
            latitude: data.latitude,
            longitude: data.longitude,
          });
        })
        .catch((err: any) => {
          const errMsg = err?.response?.data?.message || 'Erreur lors du chargement du client';
          setError(errMsg);
        })
        .finally(() => setLoading(false));
    } else {
      form.setFieldsValue({
        country: "Côte d'Ivoire",
      });
    }
  }, [id, form]);

  const handleSubmit = async (values: any) => {
    setError(null);
    setSubmitting(true);
    try {
      if (id) {
        await customersService.updateCustomer(Number(id), values);
        snackActions.success('Client modifié avec succès');
        setTimeout(() => navigate(`/customers/${id}`), 1000);
      } else {
        const created = await customersService.createCustomer(values);
        snackActions.success('Client créé avec succès');
        setCreatedCustomer(created || { ...values });
        setCreatedModalVisible(true);
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || err?.response?.data?.error || 'Erreur lors de la sauvegarde du client';
      setError(errMsg);
      snackActions.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '80px 24px', textAlign: 'center', backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16, color: '#64748B', fontWeight: 500 }}>Chargement des données du client...</div>
      </div>
    );
  }

  const customerInitials = customer?.first_name 
    ? `${customer.first_name.charAt(0)}${customer.last_name ? customer.last_name.charAt(0) : ''}`.toUpperCase()
    : 'CL';

  return (
    <div style={{ padding: '24px', backgroundColor: '#F8FAFC', minHeight: '100vh', maxWidth: 1200, margin: '0 auto' }}>
      {/* En-tête supérieur blanc fixe au scroll */}
      <Card
        style={{
          position: 'sticky',
          top: 64,
          zIndex: 90,
          marginBottom: 20,
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(8px)',
        }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(id ? `/customers/${id}` : '/clients')}
              style={{ borderRadius: 8 }}
            >
              Retour
            </Button>
            <Avatar
              size={48}
              style={{
                backgroundColor: '#0A543D',
                color: '#FFFFFF',
                fontWeight: 600,
                fontSize: 16,
                flexShrink: 0
              }}
            >
              {id ? customerInitials : <UserOutlined />}
            </Avatar>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#0F172A' }}>
                  {id ? 'Modifier le client' : 'Ajouter un client'}
                </Title>
                {id && getStatusTag(customer?.user?.status || form.getFieldValue('status'))}
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {id
                  ? `Fiche Client #${id} • ${customer?.first_name || ''} ${customer?.last_name || ''} ${customer?.company_name ? `(${customer.company_name})` : ''}`
                  : 'Renseignez les coordonnées et informations pour créer un nouveau compte client'}
              </Text>
            </div>
          </div>

          <Space size="middle">
            <Button
              onClick={() => navigate(id ? `/customers/${id}` : '/clients')}
              style={{ borderRadius: 8 }}
            >
              Annuler
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={submitting}
              onClick={() => form.submit()}
              style={{
                backgroundColor: '#0A543D',
                borderColor: '#0A543D',
                borderRadius: 8,
                fontWeight: 600
              }}
            >
              {id ? 'Enregistrer les modifications' : 'Créer le client'}
            </Button>
          </Space>
        </div>
      </Card>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 20, borderRadius: 8 }}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ country: "Côte d'Ivoire" }}>
        {/* Section 1 : Identité & Coordonnées */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: 'rgba(10, 84, 61, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0A543D'
                }}
              >
                <UserOutlined />
              </div>
              <div>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>Identité & Coordonnées</span>
                <div style={{ fontSize: 12, fontWeight: 400, color: '#64748B' }}>
                  Informations d'identification et contacts principaux
                </div>
              </div>
            </div>
          }
          style={{
            marginBottom: 20,
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 12]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Prénom</span>}
                name="first_name"
                rules={[{ required: true, message: 'Le prénom est requis' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: Tidiane"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Nom</span>}
                name="last_name"
                rules={[{ required: true, message: 'Le nom est requis' }]}
              >
                <Input
                  prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: SAMASSI"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Email</span>}
                name="email"
                rules={[{ type: 'email', message: 'Email invalide' }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: client@exemple.com"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Téléphone</span>}
                name="phone"
                rules={[{ required: true, message: 'Le téléphone est requis' }]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: 2250508698284"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Sexe</span>}
                name="gender"
              >
                <Select size="large" style={{ borderRadius: 8 }} placeholder="Sélectionner le sexe">
                  <Option value="homme">Homme</Option>
                  <Option value="femme">Femme</Option>
                  <Option value="autre">Autre</Option>
                </Select>
              </Form.Item>
            </Col>
            {id && (
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span style={{ fontWeight: 500, color: '#334155' }}>Statut du compte</span>}
                  name="status"
                >
                  <Select size="large" style={{ borderRadius: 8 }} placeholder="Sélectionner le statut">
                    <Option value="active">Actif</Option>
                    <Option value="inactive">Inactif</Option>
                    <Option value="pending">En attente</Option>
                    <Option value="suspended">Suspendu</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        {/* Section 2 : Profil Professionnel */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: 'rgba(238, 189, 27, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#B45309'
                }}
              >
                <ShopOutlined />
              </div>
              <div>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>Profil Professionnel</span>
                <div style={{ fontSize: 12, fontWeight: 400, color: '#64748B' }}>
                  Structure d'appartenance et classification client
                </div>
              </div>
            </div>
          }
          style={{
            marginBottom: 20,
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 12]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Entreprise (Raison sociale)</span>}
                name="company_name"
              >
                <Input
                  prefix={<ShopOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Laisser vide si particulier"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Type d'entreprise</span>}
                name="company_type"
              >
                <Select size="large" style={{ borderRadius: 8 }} placeholder="Sélectionner le type">
                  <Option value="particulier">Particulier</Option>
                  <Option value="sante">Santé</Option>
                  <Option value="commerce">Commerce</Option>
                  <Option value="entreprise">Entreprise</Option>
                  <Option value="administration">Administration</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Section 3 : Localisation & Adresse */}
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#1D4ED8'
                }}
              >
                <EnvironmentOutlined />
              </div>
              <div>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#0F172A' }}>Localisation & Adresse</span>
                <div style={{ fontSize: 12, fontWeight: 400, color: '#64748B' }}>
                  Zone géographique d'intervention et positionnement GPS
                </div>
              </div>
            </div>
          }
          style={{
            marginBottom: 20,
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)'
          }}
          bodyStyle={{ padding: '24px' }}
        >
          <Row gutter={[24, 12]}>
            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Pays</span>}
                name="country"
              >
                <Select size="large" style={{ borderRadius: 8 }}>
                  <Option value="Côte d'Ivoire">Côte d'Ivoire</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Ville</span>}
                name="city"
              >
                <Select
                  showSearch
                  size="large"
                  style={{ borderRadius: 8 }}
                  placeholder="Sélectionner la ville"
                  onChange={(val) => {
                    if (val !== 'Abidjan') {
                      form.setFieldValue('commune', undefined);
                    }
                  }}
                >
                  <Option value="Abidjan">Abidjan</Option>
                  <Option value="Assinie">Assinie</Option>
                  <Option value="Grand-Bassam">Grand-Bassam</Option>
                  <Option value="Bingerville">Bingerville</Option>
                  <Option value="Songon">Songon</Option>
                  <Option value="Anyama">Anyama</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Commune</span>}
                name="commune"
                extra={!isAbidjan ? <span style={{ color: '#94A3B8', fontSize: 12 }}>La sélection d'une commune est réservée à la ville d'Abidjan</span> : undefined}
              >
                <Select
                  showSearch
                  size="large"
                  style={{ borderRadius: 8 }}
                  placeholder={isAbidjan ? "Sélectionner la commune" : "Non applicable hors Abidjan"}
                  disabled={!isAbidjan}
                >
                  <Option value="Abobo">Abobo</Option>
                  <Option value="Adjamé">Adjamé</Option>
                  <Option value="Attécoubé">Attécoubé</Option>
                  <Option value="Cocody">Cocody</Option>
                  <Option value="Koumassi">Koumassi</Option>
                  <Option value="Marcory">Marcory</Option>
                  <Option value="Plateau">Plateau</Option>
                  <Option value="Port-Bouët">Port-Bouët</Option>
                  <Option value="Treichville">Treichville</Option>
                  <Option value="Yopougon">Yopougon</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Latitude</span>}
                name="latitude"
              >
                <Input
                  type="number"
                  step="any"
                  prefix={<CompassOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: 5.3599517"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span style={{ fontWeight: 500, color: '#334155' }}>Longitude</span>}
                name="longitude"
              >
                <Input
                  type="number"
                  step="any"
                  prefix={<CompassOutlined style={{ color: '#94A3B8' }} />}
                  placeholder="Ex: -4.0082563"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Barre d'actions inférieure (Fixe au Scroll) */}
        <Card
          style={{
            position: 'sticky',
            bottom: 16,
            zIndex: 80,
            borderRadius: 12,
            border: '1px solid #E2E8F0',
            boxShadow: '0 -4px 16px rgba(15, 23, 42, 0.08)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(8px)',
            marginBottom: 32
          }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <span style={{ color: '#EF4444', marginRight: 4 }}>*</span> Champs requis pour l'enregistrement du client
            </Text>
            <Space size="middle">
              <Button
                size="large"
                onClick={() => navigate(id ? `/customers/${id}` : '/clients')}
                style={{ borderRadius: 8, padding: '0 20px' }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                icon={<SaveOutlined />}
                loading={submitting}
                style={{
                  backgroundColor: '#0A543D',
                  borderColor: '#0A543D',
                  borderRadius: 8,
                  fontWeight: 600,
                  padding: '0 28px'
                }}
              >
                {id ? 'Enregistrer les modifications' : 'Ajouter le client'}
              </Button>
            </Space>
          </div>
        </Card>
      </Form>

      {/* Modal de confirmation et rappel mot de passe 1ère connexion */}
      <Modal
        open={createdModalVisible}
        title={null}
        footer={null}
        closable={false}
        centered
        width={560}
        bodyStyle={{ padding: '28px 24px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: 'rgba(10, 84, 61, 0.1)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0A543D',
              fontSize: 28,
              marginBottom: 12,
            }}
          >
            <CheckCircleFilled />
          </div>
          <Title level={4} style={{ margin: 0, color: '#0F172A', fontWeight: 600 }}>
            Client créé avec succès !
          </Title>
          <Text style={{ color: '#64748B', fontSize: 14 }}>
            Le compte de <strong>{createdCustomer?.first_name} {createdCustomer?.last_name}</strong> a bien été enregistré.
          </Text>
        </div>

        {/* Encart Rappel 1ère connexion application mobile */}
        <div
          style={{
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: 12,
            padding: '18px 20px',
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <MobileOutlined style={{ color: '#0A543D', fontSize: 18 }} />
            <span style={{ fontWeight: 600, color: '#0F172A', fontSize: 14 }}>
              Consignes pour sa 1ère connexion sur l'application mobile SMART MAINTENANCE :
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Option 1 */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                padding: '12px 14px',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0A543D', marginBottom: 4 }}>
                1. Mot de passe initial par défaut
              </div>
              <div style={{ fontSize: 13, color: '#475569', marginBottom: 8 }}>
                Le client peut se connecter immédiatement avec son numéro et le mot de passe initial :
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#F1F5F9',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px dashed #CBD5E1',
                }}
              >
                <code style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', letterSpacing: 0.5 }}>
                  defaultpassword123
                </code>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  style={{ color: '#0A543D', fontWeight: 500 }}
                  onClick={() => {
                    navigator.clipboard.writeText('defaultpassword123');
                    snackActions.success('Mot de passe copié !');
                  }}
                >
                  Copier
                </Button>
              </div>
            </div>

            {/* Option 2 */}
            <div
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: 8,
                padding: '12px 14px',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0A543D', marginBottom: 4 }}>
                2. Définition de son propre mot de passe via SMS OTP
              </div>
              <div style={{ fontSize: 13, color: '#475569' }}>
                Sur l'écran de connexion mobile, le client clique sur <strong>« Mot de passe oublié »</strong> : il recevra un code SMS OTP sur son numéro pour choisir son mot de passe personnel.
              </div>
            </div>
          </div>
        </div>

        {/* Boutons d'action */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            size="large"
            onClick={() => {
              setCreatedModalVisible(false);
              navigate('/clients');
            }}
            style={{ borderRadius: 8 }}
          >
            Retour à la liste des clients
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              setCreatedModalVisible(false);
              navigate(createdCustomer?.id ? `/customers/${createdCustomer.id}` : '/clients');
            }}
            style={{
              backgroundColor: '#0A543D',
              borderColor: '#0A543D',
              borderRadius: 8,
              fontWeight: 500,
            }}
          >
            Voir la fiche client
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default CustomerForm;
